import { forwardRef } from "react";
import type { InputHTMLAttributes } from "react";
import { useFieldId } from "./useFieldId";
import { cn } from "@/lib/utils";
import Label from "./Label";
import { FieldError, fieldErrorId } from "./FieldError";
import { FieldHint, fieldHintId } from "./FieldHint";
import { fieldControlClass } from "./field-variants";
import { type ContactVerifyStatus, verifyStatusBorderClass } from "./verifyStatus";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Green/red border for verification state (ignored when ``error`` is set). */
  verifyStatus?: ContactVerifyStatus;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      verifyStatus,
      id,
      name,
      required,
      ...props
    },
    ref,
  ) => {
    const inputId = useFieldId(id, name);
    const errorId = inputId ? fieldErrorId(inputId) : undefined;
    const hintId = inputId ? fieldHintId(inputId) : undefined;
    const statusBorder = verifyStatusBorderClass(verifyStatus, Boolean(error));

    return (
      <div className="w-full">
        {label ? (
          <Label htmlFor={inputId} spacing="field">
            {label}
            {required ? <span className="text-error-500"> *</span> : null}
          </Label>
        ) : null}
        <div className="relative">
          {leftIcon ? (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
              {leftIcon}
            </div>
          ) : null}
          <input
            ref={ref}
            id={inputId}
            name={name}
            className={fieldControlClass({
              className: cn(
                leftIcon && "pl-9",
                rightIcon && "pr-9",
                statusBorder,
                className,
              ),
            })}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              error ? errorId : hint ? hintId : undefined
            }
            {...props}
          />
          {rightIcon ? (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400">
              {rightIcon}
            </div>
          ) : null}
        </div>
        {error && errorId ? (
          <FieldError id={errorId}>{error}</FieldError>
        ) : null}
        {hint && !error && hintId ? (
          <FieldHint id={hintId}>{hint}</FieldHint>
        ) : null}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
